import React from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { motion } from 'framer-motion'
import {
  FileText,
  Globe,
  LoaderCircleIcon,
  Paperclip,
  X,
} from 'lucide-react'
import PropTypes from 'prop-types'

import ArrowUpRight from '@/assets/arrow-up-right.svg?react'
import { useChatSubmit } from '@/hooks/chat/useChatSubmit'
import useIsMobileDevice from '@/hooks/utils/useIsMobileDevice'
import { useUploadVercelBlob } from '@/hooks/utils/useUploadVercelBlob'
import { useCurrentConversationStore } from '@/store/useCurrentConversationStore'
import { useItemColors } from '@/utils/useItemColors'

import { toast } from 'sonner'

import { cn } from '../../lib/utils'
import { Button } from '../ui/button'

const ACCEPTED_FILE_TYPES_ATTRIBUTE =
  'image/jpg,image/jpeg,image/png,image/gif,image/webp,application/pdf'
const ACCEPTED_FILE_TYPES = new Set(
  ACCEPTED_FILE_TYPES_ATTRIBUTE.split(',').map(type => type.trim()),
)
const ACCEPTED_IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp'])
const ACCEPTED_FILE_EXTENSIONS = new Set([...ACCEPTED_IMAGE_EXTENSIONS, 'pdf'])
const KNOWN_IMAGE_EXTENSIONS = new Set([
  ...ACCEPTED_IMAGE_EXTENSIONS,
  'heic',
  'heif',
  'bmp',
  'tiff',
  'tif',
  'svg',
  'avif',
])
const DOCUMENT_EXTENSIONS = new Set([
  'doc',
  'docx',
  'txt',
  'rtf',
  'pages',
  'key',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
  'csv',
  'odt',
  'odp',
  'ods',
])
const DOCUMENT_MIME_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'application/rtf',
])
const SUPPORTED_IMAGE_FORMATS_LABEL = 'JPG, JPEG, PNG, GIF, WEBP'
const MAX_TOTAL_FILE_SIZE = 50 * 1024 * 1024 // 50MB

const getFileExtension = file =>
  file?.name?.split('.').pop()?.toLowerCase() ?? ''

const isAcceptedFile = file => {
  if (!file) return false
  if (file.type && ACCEPTED_FILE_TYPES.has(file.type)) return true

  const extension = getFileExtension(file)
  if (!extension) return false
  return ACCEPTED_FILE_EXTENSIONS.has(extension)
}

const isLikelyImageFile = file => {
  if (!file) return false
  if (file.type?.startsWith('image/')) return true
  const extension = getFileExtension(file)
  if (!extension) return false
  return KNOWN_IMAGE_EXTENSIONS.has(extension)
}

const isLikelyDocumentFile = file => {
  if (!file) return false
  const type = file.type ?? ''
  if (DOCUMENT_MIME_TYPES.has(type)) return true
  const extension = getFileExtension(file)
  if (!extension) return false
  return DOCUMENT_EXTENSIONS.has(extension)
}

const buildUnsupportedFileToastContent = files => {
  if (!files.length) return null

  const hasImage = files.some(isLikelyImageFile)
  const hasDocument = files.some(isLikelyDocumentFile)

  if (hasImage && !hasDocument) {
    return {
      title: 'Use a supported image format',
      description: `We accept ${SUPPORTED_IMAGE_FORMATS_LABEL} images.`,
      variant: 'destructive',
    }
  }

  if (hasDocument && !hasImage) {
    return {
      title: 'Convert documents to PDF',
      description: 'Convert this document to PDF and try again.',
      variant: 'destructive',
    }
  }

  if (hasImage && hasDocument) {
    return {
      title: 'Adjust a few files before uploading',
      description: `Images must be ${SUPPORTED_IMAGE_FORMATS_LABEL}. Documents should be converted to PDF first.`,
      variant: 'destructive',
    }
  }

  return {
    title: 'Unsupported file',
    description: `Supported formats: ${SUPPORTED_IMAGE_FORMATS_LABEL}, PDF.`,
    variant: 'destructive',
  }
}

/**
 * ChatInput
 *
 * `variant="default"` (default): full bottom composer layout used in chat views.
 * `variant="embedded"`: renders only the composer itself (no outer layout wrappers),
 * intended for embedding inside a parent "glass" container.
 */
const ChatInput = ({ variant = 'default' }) => {
  const embedded = variant === 'embedded'

  const [input, setInput] = React.useState('')
  const inputRef = React.useRef(null)
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const [attachedFiles, setAttachedFiles] = React.useState([])
  const [isWebSearchEnabled, setIsWebSearchEnabled] = React.useState(false)

  const fileInputRef = React.useRef(null)
  const containerRef = React.useRef(null)
  const menuRef = React.useRef(null)

  const { submitUserMessage } = useChatSubmit()
  const streamPhase = useCurrentConversationStore(state => state.streamPhase)
  const isStreaming = streamPhase === 'streaming' || streamPhase === 'loading'
  const isConvoLoading = useCurrentConversationStore(state => state.loading)

  const isMobileDevice = useIsMobileDevice()
  const { uploadVercelBlob } = useUploadVercelBlob()
  const { getIdTokenClaims } = useAuth0()

  const hasPendingUploads = React.useMemo(
    () => attachedFiles.some(file => file.isUploading),
    [attachedFiles],
  )

  const uploadedFilePathnames = React.useMemo(
    () =>
      attachedFiles
        .filter(file => !file.isUploading && file.pathname)
        .map(file => file.pathname),
    [attachedFiles],
  )

  React.useEffect(() => {
    if (inputRef.current && !isMobileDevice) {
      inputRef.current.focus()
    }
  }, [isConvoLoading, isMobileDevice])

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = `${inputRef.current.scrollHeight + 1}px`
    }
  }, [input])

  React.useEffect(() => {
    if (!isMenuOpen) return

    const handleClickOutside = e => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !e.target.closest('[data-menu-trigger]')
      ) {
        setIsMenuOpen(false)
      }
    }

    const handleEscapeKey = e => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isMenuOpen])

  React.useEffect(() => {
    const handlePaste = e => {
      const items = e.clipboardData?.items
      if (!items) return

      const files = []
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const file = items[i].getAsFile()
          if (file) files.push(file)
        }
      }

      if (files.length > 0) {
        e.preventDefault()
        handleFilesAdded(files)
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('paste', handlePaste)
      return () => container.removeEventListener('paste', handlePaste)
    }
  }, [])

  React.useEffect(() => {
    const handleDragOver = e => {
      e.preventDefault()
      e.stopPropagation()
    }

    const handleDrop = e => {
      e.preventDefault()
      e.stopPropagation()

      const files = Array.from(e.dataTransfer?.files || [])
      if (files.length > 0) {
        handleFilesAdded(files)
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('dragover', handleDragOver)
      container.addEventListener('drop', handleDrop)
      return () => {
        container.removeEventListener('dragover', handleDragOver)
        container.removeEventListener('drop', handleDrop)
      }
    }
  }, [])

  const handleSubmit = async e => {
    e.preventDefault()
    if (!input.trim() || isStreaming || isConvoLoading || hasPendingUploads)
      return

    try {
      await submitUserMessage({
        content: input.trim(),
        file_pathnames: uploadedFilePathnames,
        webSearchEnabled: isWebSearchEnabled,
      })
      setInput('')
      setIsWebSearchEnabled(false)
      setAttachedFiles(prev => {
        prev.forEach(file => {
          if (file.preview) URL.revokeObjectURL(file.preview)
        })
        return []
      })
    } catch (error) {
      console.error(error)
    }
  }

  const handleFilesAdded = async files => {
    if (!files?.length) return

    const acceptedFiles = []
    const rejectedFiles = []

    files.forEach(file => {
      if (isAcceptedFile(file)) {
        acceptedFiles.push(file)
      } else {
        rejectedFiles.push(file)
      }
    })

    if (rejectedFiles.length > 0) {
      const toastContent = buildUnsupportedFileToastContent(rejectedFiles)
      if (toastContent) {
        toast(toastContent)
      }
    }

    if (!acceptedFiles.length) return

    const existingTotalSize = attachedFiles.reduce(
      (total, fileObj) => total + (fileObj.file?.size ?? 0),
      0,
    )
    const incomingTotalSize = acceptedFiles.reduce(
      (total, file) => total + (file?.size ?? 0),
      0,
    )

    if (existingTotalSize + incomingTotalSize > MAX_TOTAL_FILE_SIZE) {
      toast.error('Total attachment size exceeded', {
        description: 'Please keep the combined file size under 50MB.',
      })
      return
    }

    const filesWithProgress = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      uploadProgress: 0,
      isUploading: true,
      pathname: null,
      preview: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : null,
    }))

    setAttachedFiles(prev => [...prev, ...filesWithProgress])

    filesWithProgress.forEach(fileObj => {
      uploadFile(fileObj)
    })
  }

  const handleFileUpload = e => {
    const files = Array.from(e.target.files || [])
    handleFilesAdded(files)
    setIsMenuOpen(false)
  }

  const uploadFile = async fileObj => {
    let uploadResponse
    let uploadPath

    try {
      const idTokenClaims = await getIdTokenClaims()
      const userId = idTokenClaims.sub.slice(-10)

      const extension = fileObj.file.name.split('.').pop()
      const timestamp = Date.now()
      uploadPath = `${userId}/${timestamp}${fileObj.file.name
        .split('.')[0]
        .slice(-10)}.${extension}`

      uploadResponse = await uploadVercelBlob(fileObj.file, uploadPath)
    } catch (error) {
      setAttachedFiles(prev => prev.filter(f => f.id !== fileObj.id))
      toast.error(`Error uploading ${fileObj.file.name}`, {
        variant: 'destructive',
      })
      return
    }

    if (!uploadResponse?.pathname) {
      toast.error(`Error uploading ${fileObj.file.name}`, {
        description: 'Failed to upload file',
      })
      return
    }

    const { pathname } = uploadResponse
    setAttachedFiles(prev =>
      prev.map(f =>
        f.id === fileObj.id
          ? {
              ...f,
              uploadProgress: 100,
              isUploading: false,
              pathname,
            }
          : f,
      ),
    )
  }

  const removeFile = async fileObj => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileObj.id))
    if (fileObj.preview) {
      URL.revokeObjectURL(fileObj.preview)
    }
  }

  const handleWebSearchToggle = () => {
    setIsWebSearchEnabled(!isWebSearchEnabled)
    setIsMenuOpen(false)
  }

  const menuOptions = [
    {
      icon: Paperclip,
      label: 'Add photos & files',
      onClick: () => fileInputRef.current?.click(),
    },
    { icon: Globe, label: 'Web search', onClick: handleWebSearchToggle },
  ]

  const getFileInfo = file => {
    const extension = file.name.split('.').pop()?.toLowerCase()
    if (['xls', 'xlsx', 'csv'].includes(extension)) {
      return { type: 'Spreadsheet', color: 'bg-green-9' }
    }
    if (['doc', 'docx', 'txt'].includes(extension)) {
      return { type: 'Document', color: 'bg-blue-9' }
    }
    if (['pdf'].includes(extension)) {
      return { type: 'PDF', color: 'bg-red-9' }
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return { type: 'Image', color: 'bg-purple-9' }
    }
    return { type: 'File', color: 'bg-gray-9' }
  }

  const composerShellClass = cn(
    'relative flex w-full flex-col bg-filter',
     embedded
       ? // Embedded mode: parent "glass" container owns the look.
         'rounded-none bg-transparent p-0 shadow-none outline-none'
      : 'rounded-[28px] bg-gray-subtle p-3 backdrop-blur-xl dark:bg-[rgba(48,52,54,0.6)]',
  )

  const textareaClass = cn(
    'block w-full resize-none border-0 px-0 py-2 !ring-0 placeholder:ps-px focus:outline-none focus-visible:outline-none focus-visible:ring-0',
    'appearance-none shadow-none',
    'overflow-y-auto',
    embedded
      ? 'text-white/90 placeholder:text-white/45'
      : cn(
          'new-chat:text-black/80',
          'new-chat:placeholder:text-black/50 dark:new-chat:placeholder:text-black/50',
          'text-black/90 placeholder:text-black/60',
          'dark:text-white/95 dark:placeholder:text-white/70',
        ),
  )

  const sendButtonClass = cn(
    'flex size-9 items-center justify-center rounded-full transition-colors',
    embedded
      ? cn(
          'bg-white/12 text-white',
          'border border-white/15',
          'hover:bg-white/18',
          'disabled:opacity-40',
        )
      : cn(
          'bg-black text-white transition-colors hover:opacity-70',
          !isStreaming && [
            'disabled:bg-black/20 disabled:text-black/30 disabled:hover:opacity-100',
            'new-chat:bg-white/10 new-chat:text-black/50 new-chat:disabled:bg-white/10 new-chat:disabled:text-white/30',
          ],
          'new-chat:bg-black/85 new-chat:text-white/80 dark:bg-white/85 dark:text-black dark:focus-visible:outline-white',
          (isStreaming || isConvoLoading) && 'animate-ethereal-pulse',
        ),
  )

  const composer = (
    <form
      onSubmit={handleSubmit}
      className="w-full"
      data-type="unified-composer"
      data-chat-input-variant={variant}
    >
      <div
        id="composer"
        ref={containerRef}
        className={composerShellClass}
      >
        {attachedFiles.length > 0 && (
          <div className={cn('mb-3 flex flex-wrap gap-2', embedded ? 'px-1' : 'px-2')}>
            {attachedFiles.map(fileObj => {
              const fileInfo = getFileInfo(fileObj.file)
              const isImage = fileObj.file.type.startsWith('image/')

              return (
                <div
                  key={fileObj.id}
                  className={cn(
                    'relative flex items-center gap-3 rounded-xl px-3 py-2',
                    'border',
                    embedded
                      ? 'border-white/10 bg-white/8'
                      : 'bg-whitea-11 border-graya-4 new-chat:bg-graya-7 dark:bg-graya-7 dark:border-graya-7',
                  )}
                >
                  {isImage && fileObj.preview ? (
                    <div className="relative size-10 overflow-hidden rounded-lg">
                      <img
                        src={fileObj.preview || '/placeholder.svg'}
                        alt={fileObj.file.name}
                        className="size-full object-cover"
                      />
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'flex size-10 items-center justify-center rounded-lg',
                        fileInfo.color,
                      )}
                    >
                      <FileText size={20} className="text-white" />
                    </div>
                  )}

                  <div className="min-w-0 max-w-[200px] flex-1">
                    <p
                      className={cn(
                        'truncate font-medium text-sm',
                        embedded ? 'text-white/85' : 'text-graya-normal',
                      )}
                    >
                      {fileObj.file.name}
                    </p>
                    <p className={cn('text-xs', embedded ? 'text-white/50' : 'text-graya-dim')}>
                      {fileInfo.type}
                    </p>
                  </div>

                  {fileObj.isUploading && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b-xl bg-black/10 dark:bg-gray-700">
                      <div
                        className="h-full bg-blue-9 transition-all duration-300"
                        style={{ width: `${fileObj.uploadProgress}%` }}
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => removeFile(fileObj)}
                    className={cn(
                      'flex size-5 items-center justify-center rounded-full',
                      embedded ? 'bg-white/10 hover:bg-white/15' : 'bg-graya-ui',
                    )}
                  >
                    <X size={12} />
                  </button>
                </div>
              )
            })}
          </div>
        )}

        <div className={cn('flex items-end gap-2', embedded ? '' : '')}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_TYPES_ATTRIBUTE}
            onChange={handleFileUpload}
            className="hidden"
          />

          <div className="relative flex w-full flex-auto items-center gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit(e)
                }
              }}
              placeholder="Ask Project Pilot..."
              rows={1}
              className={textareaClass}
              style={{
                minHeight: '47px',
                maxHeight: '25vh',
                backgroundColor: 'transparent',
                background: 'transparent',
                boxShadow: 'none',
                outline: 'none',
                WebkitAppearance: 'none',
              }}
            />

            <Button
              aria-label="Send prompt"
              type="submit"
              size="icon"
              disabled={
                !input.trim() ||
                isStreaming ||
                isConvoLoading ||
                hasPendingUploads
              }
              className={sendButtonClass}
            >
              <motion.div
                asChild
                animate={
                  isStreaming
                    ? { scale: 0.8, opacity: 0.5 }
                    : { scale: 1, opacity: 1 }
                }
                transition={{ duration: 0.2 }}
              >
                {isStreaming ? (
                  <LoaderCircleIcon
                    size={16}
                    className={cn(
                      embedded
                        ? 'text-white/80'
                        : 'icon-md text-white transition-transform dark:text-black/80',
                      'animate-gentle-spin',
                    )}
                  />
                ) : (
                  <ArrowUpRight
                    size={16}
                    disabled={
                      !input.trim() ||
                      isStreaming ||
                      isConvoLoading ||
                      hasPendingUploads
                    }
                    className={cn(
                      embedded ? 'text-white/80' : 'icon-md text-white transition-transform duration-200 dark:text-black/80',
                      'group-hover:-translate-y-0.5 group-hover:translate-x-0.5',
                    )}
                  />
                )}
              </motion.div>
              <span className="sr-only">Send message</span>
            </Button>
          </div>
        </div>
      </div>
    </form>
  )

  if (embedded) {
    return <div className="w-full">{composer}</div>
  }

  return (
    <div
      id="thread-bottom-container"
      className={cn(
        'isolate z-[3] flex w-full basis-auto flex-col md:border-transparent md:pt-0 dark:border-white/20 md:dark:border-transparent',
        'new-chat:@lg/thread:grow',
      )}
      style={{ transition: 'transform 1s ease-in-out' }}
    >
      <div id="thread-bottom">
        <div className="mx-auto px-[var(--thread-content-margin)] text-base [--thread-content-margin:--spacing(4)] @[37rem]:[--thread-content-margin:--spacing(6)] @[72rem]:[--thread-content-margin:--spacing(16)]">
          <div className="mx-auto flex max-w-[var(--thread-content-max-width)] flex-1 gap-4 text-base [--thread-content-max-width:32rem] @[34rem]:[--thread-content-max-width:40rem] @[64rem]:[--thread-content-max-width:48rem] md:gap-5 lg:gap-6">
            <div className="flex justify-center empty:hidden" />
            <div className="relative z-[1] flex h-full max-w-full flex-1 flex-col">
              {composer}
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'mt-auto',
          'new-chat:mt-0 md:px-[60px]',
          'relative flex min-h-8 w-full items-center justify-center p-2 text-center text-xs',
        )}
      >
        <div
          className={cn(
            'overflow-visible',
            'new-chat:hidden',
            'text-black/45 transition-transform duration-200',
            'dark:text-white/50',
          )}
        >
          AI-generated content may be incorrect. Always verify information.
        </div>
      </div>
    </div>
  )
}

ChatInput.propTypes = {
  variant: PropTypes.oneOf(['default', 'embedded']),
}

export default ChatInput
